import clsx from "clsx";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { driverRoadTestQueryOptions } from "src/api/mvte/driver-road-test-query-options";
import useAccountEnhanced from "src/hooks/use-account-enhanced";
import { SavedExam } from "../../types/exam";
import { Question } from "../../types/question";
import { Section } from "../../types/section";
import { ExamSectionQuestions } from "./exam-section-questions";

type CompletionFormData = {
    result: Section["result"];
};

export type ExamSectionContentProps = {
    exam: SavedExam;
    onExamUpdate: (updatedExam: SavedExam) => void;
    section: Section;
    onQuestionSelection: (updatedQuestion: Question) => void;
    isEveryQuestionAnswered: boolean;
    sectionStatus: Section["status"];
    driver: { name: string };
    isExamCompleted: boolean;
};

export function ExamSectionContent({
    exam,
    onExamUpdate,
    section,
    onQuestionSelection,
    isEveryQuestionAnswered,
    sectionStatus,
    driver,
    isExamCompleted,
}: ExamSectionContentProps) {
    const account = useAccountEnhanced();
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<CompletionFormData>({
        defaultValues: {
            result: null,
        },
    });

    const updateExamMutation = driverRoadTestQueryOptions.useUpdateExam();

    const isSectionReadyToComplete =
        isEveryQuestionAnswered && sectionStatus === "in_progress";

    const onSubmit = async (data: CompletionFormData) => {
        const updatedSection: Section = {
            ...section,
            status: "completed",
            result: data.result,
        };

        const updatedSections = exam.sections.map((section) =>
            section.sectionID === updatedSection.sectionID
                ? updatedSection
                : section,
        );

        const updatedExam = {
            ...exam,
            sections: updatedSections,
        };

        try {
            await updateExamMutation.mutateAsync({
                body: {
                    exam: updatedExam,
                },
            });

            onExamUpdate(updatedExam);
        } catch (error: unknown) {
            toast.error(
                `Error saving exam: ${
                    error instanceof Error ? error.message : "Unexpected error"
                }`,
            );
            return;
        }

        const modal = document.getElementById(
            "completion_modal",
        ) as HTMLDialogElement;
        modal?.close();

        reset();
    };

    const handleModalClose = () => {
        reset();
    };

    return (
        <div>
            <div className="sticky top-0 z-10 bg-white border-b border-base-300 px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <h3 className="font-medium text-gray-800">
                        {section.sectionName}
                    </h3>
                    {sectionStatus === "completed" && section.result && (
                        <span
                            className={`badge badge-sm ${
                                section.result === "Qualified"
                                    ? "badge-success"
                                    : section.result ===
                                      "Qualified with additional training"
                                    ? "badge-warning"
                                    : "badge-error"
                            }`}
                        >
                            {section.result}
                        </span>
                    )}
                </div>
                <button
                    className="btn btn-primary btn-sm"
                    disabled={!isSectionReadyToComplete}
                    onClick={() => {
                        const modal = document.getElementById(
                            "completion_modal",
                        ) as HTMLDialogElement;
                        modal?.showModal();
                    }}
                >
                    Complete Section
                </button>
            </div>

            <ExamSectionQuestions
                questions={section.questions}
                onQuestionSelection={onQuestionSelection}
                isExamCompleted={isExamCompleted}
            />

            <dialog
                id="completion_modal"
                className="modal"
                onClose={handleModalClose}
            >
                <div className="modal-box">
                    <h3 className="font-bold text-lg mb-4">Complete Section</h3>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="mb-4">
                            <fieldset className="fieldset">
                                <legend className="fieldset-legend">
                                    Section Result
                                </legend>
                                <select
                                    className={`select select-bordered w-full ${
                                        errors.result ? "select-error" : ""
                                    }`}
                                    {...register("result", {
                                        required: "Result is required",
                                    })}
                                >
                                    <option disabled selected>
                                        Select a result
                                    </option>
                                    <option value="Qualified">Qualified</option>
                                    <option value="Qualified with additional training">
                                        Qualified with additional training
                                    </option>
                                    <option value="Not Qualified">
                                        Not Qualified
                                    </option>
                                </select>
                                {errors.result && (
                                    <span className="text-error text-sm mt-1">
                                        {errors.result.message}
                                    </span>
                                )}
                            </fieldset>
                        </div>

                        <div className="mb-4">
                            <p className="text-sm text-gray-600">
                                I, {account.account.name}, hereby certify that{" "}
                                {driver.name} is qualified on the{" "}
                                {section.sectionName} section as per standards
                                set by Mesilla Valley Transportation.
                            </p>
                        </div>

                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-ghost mr-2"
                                onClick={() => {
                                    const modal = document.getElementById(
                                        "completion_modal",
                                    ) as HTMLDialogElement;
                                    modal?.close();
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={clsx(
                                    "btn btn-primary",
                                    updateExamMutation.isPending &&
                                        "pointer-events-none opacity-50",
                                )}
                            >
                                {updateExamMutation.isPending ? (
                                    <>
                                        <span className="loading loading-spinner loading-xs" />
                                        Submitting
                                    </>
                                ) : (
                                    "Submit"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>
        </div>
    );
}
